import type { LiveAudioStreamFormat } from '@common/audio-note/audio-note.types';

export interface LivePlaybackSnapshot {
  status: 'idle' | 'streaming' | 'completed';
  format?: LiveAudioStreamFormat;
  sampleRateHz?: number;
  channels?: number;
  bufferedChunkCount: number;
  bufferedBytes: number;
}

export class StreamPlaybackController {
  private snapshot: LivePlaybackSnapshot = {
    status: 'idle',
    bufferedChunkCount: 0,
    bufferedBytes: 0,
  };

  // This scaffold intentionally tracks stream state and buffering, but leaves
  // actual PCM playback implementation to a follow-up AudioContext/Worklet pass.
  // TODO(audio-notes-ui): replace the bookkeeping-only controller with real
  // scheduled PCM playback once the UI team is ready to tune buffering.
  beginStream(params: {
    format: LiveAudioStreamFormat;
    sampleRateHz: number;
    channels: number;
  }): LivePlaybackSnapshot {
    this.snapshot = {
      status: 'streaming',
      bufferedChunkCount: 0,
      bufferedBytes: 0,
      format: params.format,
      sampleRateHz: params.sampleRateHz,
      channels: params.channels,
    };
    return this.getSnapshot();
  }

  appendChunk(dataBase64: string): LivePlaybackSnapshot {
    const bytes = decodeBase64(dataBase64);
    this.snapshot = {
      ...this.snapshot,
      status: 'streaming',
      bufferedChunkCount: this.snapshot.bufferedChunkCount + 1,
      bufferedBytes: this.snapshot.bufferedBytes + bytes.byteLength,
    };
    return this.getSnapshot();
  }

  endStream(): LivePlaybackSnapshot {
    this.snapshot = {
      ...this.snapshot,
      status: 'completed',
    };
    return this.getSnapshot();
  }

  reset(): LivePlaybackSnapshot {
    this.snapshot = {
      status: 'idle',
      bufferedChunkCount: 0,
      bufferedBytes: 0,
    };
    return this.getSnapshot();
  }

  getSnapshot(): LivePlaybackSnapshot {
    return { ...this.snapshot };
  }
}

function decodeBase64(value: string): Uint8Array {
  const binary = atob(value);
  const output = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    output[index] = binary.charCodeAt(index);
  }

  return output;
}
